import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { UserAuth } from "../context/AuthContext";
import { useTranslation } from "react-i18next";
import {
	AppBar,
	Toolbar,
	Typography,
	Button,
	Box,
	IconButton,
	Tooltip,
	useTheme,
	useMediaQuery,
	Drawer,
	List,
	ListItem,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Divider,
	Avatar,
	Chip
} from "@mui/material";
import {
	Star,
	AddBox,
	AutoAwesome,
	FormatListBulleted,
	EmojiEvents,
	ShowChart,
	Settings,
	Login,
	Logout,
	AccountCircle,
	Edit,
	AdminPanelSettings,
	Analytics,
	Description,
	BugReport,
	Menu as MenuIcon,
	Close as CloseIcon,
	MenuBook,
	DocumentScanner
} from "@mui/icons-material";
import Version from "../components/Info";
import { getUserGamificationProfile } from "../services/gamificationService";
import { db } from "../lib/Firebase-config";
import { doc, onSnapshot } from "firebase/firestore";
import { arcadeGradient, panelStyle } from "../theme";

function Navbar() {
	const Navigate = useNavigate();
	const location = useLocation();
		const { user, logout, isAdmin } = UserAuth();
	const { t } = useTranslation();
	const [stats, setStats] = useState({ xp: 0, level: 1 });
	const [drawerOpen, setDrawerOpen] = useState(false);
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
	const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

	useEffect(() => {
		if (!user?.uid) return;

		const unsubscribe = onSnapshot(doc(db, 'user_gamification', user.uid), (doc) => {
			if (doc.exists()) {
				setStats(doc.data());
			} else {
				getUserGamificationProfile(user.uid).then(setStats);
			}
		});

		return () => unsubscribe();
	}, [user]);

	const handleLogout = async () => {
		try {
			await logout();
			setDrawerOpen(false);
			Navigate("/signin");
		} catch (error) {
			console.log(error);
		}
	};

	const handleNavClick = () => {
		setDrawerOpen(false);
	};

	// Reorganized navigation - logical user flow: View → Create → Study tools
	const navItems = [
		{ label: t('nav_lists'), path: "/overview", icon: <FormatListBulleted />, title: t('nav_lists_title') },
		{ label: t('nav_create'), path: "/create-list", icon: <AddBox />, title: t('nav_create_title') },
		{ label: t('nav_magic_notes'), path: "/generate-quiz", icon: <AutoAwesome />, title: t('nav_magic_notes') },
		{ label: t('nav_smart_notes'), path: "/notes", icon: <Edit />, title: t('nav_smart_notes') },
		{ label: "AI Studio", path: "/premium-ai", icon: <DocumentScanner />, title: "AI scan and open answer validation" },
		{ label: t('nav_docs'), path: "/docs", icon: <MenuBook />, title: t('docs_title') },
	];

	// User/account items - stats first, then account management
	const userItems = [
		{ label: t('nav_dashboard'), path: "/dashboard", icon: <ShowChart />, title: t('nav_dashboard') },
		{ label: t('nav_leaderboard'), path: "/leaderboard", icon: <EmojiEvents />, title: t('nav_leaderboard') },
		{ label: t('nav_profile'), path: "/profile", icon: <AccountCircle />, title: t('nav_profile') },
		{ label: t('nav_settings'), path: "/settings", icon: <Settings />, title: t('nav_settings') },
	];

	if (isAdmin) {
		userItems.push({ label: t('nav_admin'), path: "/admin", icon: <AdminPanelSettings />, title: t('nav_admin') });
		userItems.push({ label: t('nav_statistics'), path: "/admin/statistics", icon: <Analytics />, title: t('nav_statistics') });
		userItems.push({ label: "AI Training", path: "/admin/training-patterns", icon: <Description />, title: "AI training datasets" });
		userItems.push({ label: "Error Logs", path: "/admin/logs", icon: <BugReport />, title: "AI Error Logs" });
	}

	const isActivePath = (path) => location.pathname === path;

	const renderDrawer = () => (
		<Drawer
			anchor="right"
			open={drawerOpen}
			onClose={() => setDrawerOpen(false)}
			slotProps={{
				paper: {
					sx: {
						width: 280,
						background: theme.palette.mode === 'dark'
							? 'linear-gradient(180deg, rgba(15, 23, 42, 0.96) 0%, rgba(30, 41, 59, 0.98) 100%)'
							: 'linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(240, 249, 255, 0.98) 100%)',
						borderLeft: `1px solid ${theme.palette.divider}`,
						boxShadow: theme.palette.mode === 'dark'
							? '-12px 0 32px rgba(2, 6, 23, 0.35)'
							: '-12px 0 32px rgba(15, 23, 42, 0.12)'
					}
				}
			}}
		>
			<Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
					<Box component="img" src="/logo.png" alt="Logo" sx={{ width: 32, height: 32 }} />
					<Typography variant="h6" fontWeight="bold" color="primary.main">
						QuizBoss
					</Typography>
				</Box>
				<IconButton onClick={() => setDrawerOpen(false)} size="small">
					<CloseIcon />
				</IconButton>
			</Box>

			{user && (
				<Box sx={{ px: 2, pb: 2 }}>
					<Box sx={{
						p: 2,
						borderRadius: 3,
						...panelStyle(theme, { tint: 'secondary' })
					}}>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
							<Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
								{user.displayName?.[0]?.toUpperCase() || 'U'}
							</Avatar>
							<Box sx={{ flex: 1, minWidth: 0 }}>
								<Typography variant="subtitle2" fontWeight="bold" noWrap>
									{user.displayName}
								</Typography>
								<Typography variant="caption" color="text.secondary" noWrap>
									{user.email}
								</Typography>
							</Box>
						</Box>
						<Box sx={{ display: 'flex', gap: 1 }}>
							<Chip
								icon={<Star sx={{ fontSize: 14 }} />}
								label={stats.xp || 0}
								size="small"
								sx={{
									bgcolor: 'rgba(251, 191, 36, 0.16)',
									color: '#b45309',
									fontWeight: 'bold',
									border: '1px solid rgba(251, 191, 36, 0.24)',
									'& .MuiChip-icon': { color: '#b45309' }
								}}
							/>
							<Chip
								label={t('nav_level', { level: stats.level || 1 })}
								size="small"
								color="primary"
								variant="outlined"
								sx={{ fontWeight: 'bold' }}
							/>
						</Box>
					</Box>
				</Box>
			)}

			<Divider />

			<List sx={{ px: 1, py: 2 }}>
				<Typography variant="overline" sx={{ px: 2, color: 'text.secondary', fontWeight: 600 }}>
					{t('nav_content') || 'Content'}
				</Typography>
				{navItems.map((item) => (
					<ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
						<ListItemButton
							component={Link}
							to={item.path}
							onClick={handleNavClick}
							selected={isActivePath(item.path)}
							sx={{
								borderRadius: 2,
								mx: 1,
								'&.Mui-selected': {
									bgcolor: 'primary.main',
									color: 'primary.contrastText',
									'&:hover': { bgcolor: 'primary.dark' },
									'& .MuiListItemIcon-root': { color: 'primary.contrastText' }
								}
							}}
						>
							<ListItemIcon sx={{ minWidth: 40, color: isActivePath(item.path) ? 'inherit' : 'primary.main' }}>
								{item.icon}
							</ListItemIcon>
							<ListItemText primary={item.label} slotProps={{ primary: { fontWeight: 500 } }} />
						</ListItemButton>
					</ListItem>
				))}
			</List>

			<Divider />

			<List sx={{ px: 1, py: 2 }}>
				<Typography variant="overline" sx={{ px: 2, color: 'text.secondary', fontWeight: 600 }}>
					{t('nav_account') || 'Account'}
				</Typography>
				{userItems.map((item) => (
					<ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
						<ListItemButton
							component={Link}
							to={item.path}
							onClick={handleNavClick}
							selected={isActivePath(item.path)}
							sx={{
								borderRadius: 2,
								mx: 1,
								'&.Mui-selected': {
									bgcolor: 'primary.main',
									color: 'primary.contrastText',
									'&:hover': { bgcolor: 'primary.dark' },
									'& .MuiListItemIcon-root': { color: 'primary.contrastText' }
								}
							}}
						>
							<ListItemIcon sx={{ minWidth: 40, color: isActivePath(item.path) ? 'inherit' : 'secondary.main' }}>
								{item.icon}
							</ListItemIcon>
							<ListItemText primary={item.label} slotProps={{ primary: { fontWeight: 500 } }} />
						</ListItemButton>
					</ListItem>
				))}
			</List>

			<Box sx={{ mt: 'auto', p: 2 }}>
				<Button
					fullWidth
					variant="outlined"
					color="error"
					startIcon={<Logout />}
					onClick={handleLogout}
					sx={{ borderRadius: 2, fontWeight: 600 }}
				>
					{t('nav_logout')}
				</Button>
			</Box>
		</Drawer>
	);

	return (
		<>
			<AppBar
				position="sticky"
				elevation={0}
				sx={{
					background: arcadeGradient(theme),
					borderBottom: `1px solid rgba(255,255,255,0.16)`,
					backdropFilter: 'blur(14px)',
					boxShadow: '0 10px 30px rgba(15, 23, 42, 0.18)'
				}}
			>
				<Toolbar sx={{
					justifyContent: 'space-between',
					minHeight: { xs: 56, sm: 64 },
					px: { xs: 1, sm: 2, md: 3 }
				}}>
					{/* Brand / Logo */}
					<Box sx={{ display: 'flex', alignItems: 'center' }}>
						<Button
							component={Link}
							to="/"
							color="inherit"
							startIcon={<Box component="img" src="/logo.png" alt="Logo" sx={{ width: 28, height: 28 }} />}
							sx={{
								fontWeight: 700,
								fontSize: { xs: '1rem', sm: '1.1rem' },
								textTransform: 'none',
								letterSpacing: 0,
								borderRadius: 2,
								'&:hover': { bgcolor: 'rgba(255,255,255,0.12)' }
							}}
						>
							{!isSmallMobile && user ? t('app_title_user', { name: user.displayName }) : 'QuizBoss'}
						</Button>
						{!isMobile && (
							<Chip
								label={`v${Version.Version.version}`}
								size="small"
								sx={{
									ml: 1,
									bgcolor: 'rgba(255,255,255,0.16)',
									color: 'rgba(255,255,255,0.9)',
									fontWeight: 500,
									fontSize: '0.7rem',
									height: 22,
									border: '1px solid rgba(255,255,255,0.16)'
								}}
							/>
						)}
					</Box>

					{/* Desktop Navigation */}
					{!isMobile && user && (
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
							{/* User Stats Badge */}
							<Box sx={{
								display: 'flex',
								alignItems: 'center',
								bgcolor: 'rgba(255,255,255,0.12)',
								borderRadius: 3,
								px: 2,
								py: 0.75,
								mr: 2,
								border: '1px solid rgba(255,255,255,0.14)',
								boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.12)'
							}}>
								<Tooltip title={t('nav_points')}>
										<Box sx={{ display: 'flex', alignItems: 'center', color: '#fef08a', fontWeight: 700 }}>
										<Star sx={{ fontSize: 18, mr: 0.5 }} />
										{stats.xp || 0}
									</Box>
								</Tooltip>
								<Box sx={{
									width: 1,
									height: 16,
									bgcolor: 'rgba(255,255,255,0.2)',
									mx: 1.5
								}} />
								<Tooltip title="Current Level">
									<Typography sx={{ color: 'white', fontWeight: 600, fontSize: '0.875rem' }}>
										{t('nav_level', { level: stats.level || 1 })}
									</Typography>
								</Tooltip>
							</Box>

							{/* Main Nav Items */}
							<Box sx={{
								display: 'flex',
								alignItems: 'center',
								gap: 0.5,
								borderRight: '1px solid rgba(255,255,255,0.15)',
								pr: 1,
								mr: 1
							}}>
								{navItems.map((item) => (
									<Tooltip key={item.path} title={item.title}>
										<Button
											component={Link}
											to={item.path}
											color="inherit"
											startIcon={item.icon}
											sx={{
												fontWeight: 600,
												fontSize: '0.875rem',
												textTransform: 'none',
												px: 1.5,
												py: 0.75,
												borderRadius: 2,
												bgcolor: isActivePath(item.path) ? 'rgba(255,255,255,0.2)' : 'transparent',
												border: '1px solid transparent',
												'&:hover': { bgcolor: 'rgba(255,255,255,0.15)', borderColor: 'rgba(255,255,255,0.18)' }
											}}
										>
											{item.label}
										</Button>
									</Tooltip>
								))}
							</Box>

							{/* User Items */}
							<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
								{userItems.map((item) => (
									<Tooltip key={item.path} title={item.title}>
										<IconButton
											component={Link}
											to={item.path}
											color="inherit"
											sx={{
												bgcolor: isActivePath(item.path) ? 'rgba(255,255,255,0.2)' : 'transparent',
												border: '1px solid transparent',
												'&:hover': { bgcolor: 'rgba(255,255,255,0.15)', borderColor: 'rgba(255,255,255,0.18)' }
											}}
										>
											{item.icon}
										</IconButton>
									</Tooltip>
								))}
							</Box>

							{/* Logout */}
							<Tooltip title={t('nav_logout')}>
								<IconButton
									color="inherit"
									onClick={handleLogout}
								sx={{
									ml: 1,
									border: '1px solid transparent',
									'&:hover': { bgcolor: 'rgba(255,255,255,0.15)', borderColor: 'rgba(255,255,255,0.18)' }
								}}
							>
									<Logout />
								</IconButton>
							</Tooltip>
						</Box>
					)}

					{/* Mobile: Hamburger Menu */}
					{isMobile && user && (
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
							{/* Compact Stats Badge */}
							<Box sx={{
								display: 'flex',
								alignItems: 'center',
								bgcolor: 'rgba(255,255,255,0.12)',
								borderRadius: 2,
								px: 1.5,
								py: 0.5,
								border: '1px solid rgba(255,255,255,0.14)'
							}}>
								<Star sx={{ fontSize: 16, color: '#fde047', mr: 0.5 }} />
								<Typography sx={{ color: 'white', fontWeight: 700, fontSize: '0.8rem' }}>
									{stats.xp || 0}
								</Typography>
							</Box>

							<IconButton
								color="inherit"
								onClick={() => setDrawerOpen(true)}
								sx={{
									bgcolor: 'rgba(255,255,255,0.1)',
									border: '1px solid rgba(255,255,255,0.14)',
									'&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
								}}
							>
								<MenuIcon />
							</IconButton>
						</Box>
					)}

					{/* Login Button (when not logged in) */}
					{!user && (
						<Button
							component={Link}
							to="/signin"
							color="inherit"
							startIcon={<Login />}
							sx={{
								fontWeight: 600,
								textTransform: 'none',
								borderRadius: 2,
								px: 2,
								bgcolor: 'rgba(255,255,255,0.1)',
								border: '1px solid rgba(255,255,255,0.16)',
								'&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
							}}
						>
							{t('nav_login')}
						</Button>
					)}
				</Toolbar>
			</AppBar>

			{/* Mobile Drawer */}
			{user && renderDrawer()}
		</>
	);
}

export default Navbar;
